import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { handleGetBlogs } from "../../shared/controllers/blogController";
import { unwrapApiArray } from "../../shared/utils/apiResponse";
import LoadingGrid from "../../utils/loadingGrid";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import landingAsset from "../utils/assetPaths";

interface BlogItem {
    id: string | number;
    title: string;
    excerpt: string;
    image?: string;
    created_at: string;
}

const Blog: React.FC = () => {
    const [blogs, setBlogs] = useState<BlogItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const params = { type: "Story" };
                const data = await handleGetBlogs<BlogItem[]>("user", params);
                setBlogs(unwrapApiArray<BlogItem>(data));
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchBlogs();
    }, []);

    return (
        <>
            <Navbar />
            <div
                style={{
                    backgroundImage: `url(${landingAsset("herobg.svg")})`,
                    backgroundSize: "cover",
                }}
                className="h-[500px] md:h-[400px] relative w-full flex justify-center items-center flex-col "
            >
                <div
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 17.83%)",
                    }}
                    className=" h-full w-full absolute left-0 top-0 flex flex-col items-center justify-center p-4 text-center"
                >
                    <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-black font-Manrope uppercase tracking-tight uppercase">SACA Stories</h1>
                    <p className=" mt-6 text-center font-Manrope text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed italic">
                        Behind the game. Discover inspiring journeys, match highlights, and the heartbeat of SACA's global community.
                    </p>
                </div>
            </div>

            <div className="py-24 bg-white px-4 md:px-14">
                <div className="max-w-7xl mx-auto">
                    <div className="space-y-4 mb-20 text-center max-w-2xl mx-auto">
                        <h3 className="text-green-700 font-black uppercase tracking-[0.25em] text-sm md:text-base">The Pitch Feed</h3>
                        <h2 className="text-4xl md:text-6xl font-black text-gray-900 uppercase tracking-tighter">Latest Highlights</h2>
                    </div>

                    {loading ? (
                        <LoadingGrid count={6} />
                    ) : blogs.length === 0 ? (
                        <div className="bg-gray-50 py-32 rounded-[64px] text-center border-2 border-dashed border-gray-100 italic font-bold text-gray-400 text-2xl">
                            No stories have been published yet. Check back soon.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                            {blogs.map((blog, i) => (
                                <div key={i} className="group flex flex-col font-Manrope transition-all duration-500">
                                    <div className="w-full aspect-[4/3] bg-gray-100 rounded-[56px] overflow-hidden relative shadow-lg hover:shadow-2xl transition-all duration-700">
                                        <img src={blog.image || landingAsset("stadia.svg")} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                                        <div className="absolute top-8 left-8">
                                            <span className="px-6 py-2 bg-white/90 backdrop-blur-md rounded-2xl font-black text-[10px] uppercase tracking-widest text-green-700 shadow-xl">
                                                {new Date(blog.created_at).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="pt-10 px-4 space-y-6">
                                        <h3 className="text-3xl font-black text-gray-900 group-hover:text-green-700 transition-colors uppercase tracking-tight leading-[0.9]">{blog.title}</h3>
                                        <p className="text-gray-500 font-bold italic line-clamp-2 text-lg leading-relaxed">{blog.excerpt}</p>
                                        <div className="pt-6 border-t border-gray-100">
                                            <Link to={`/blog/${blog.id}`} className="inline-flex items-center gap-2 text-gray-900 font-black uppercase tracking-widest text-xs hover:text-green-700 transition-colors">
                                                Read Full Story <span className="text-green-700">→</span>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Banner />
            <Footer />
        </>
    );
};

export default Blog;
